import { useSelector } from 'react-redux';
import { isEmpty, uniq } from 'lodash';
import {
  getFirstAvailableDeparture,
  parseTime,
  trackError,
} from 'utils/helpers';
import { useNextDeparturesQuery } from 'redux/api_slice/service';

/**
 * Return next two departures, one for to island ship, another for from island ship
 */
export const useNextDepartures = (forwardSegmentId, returnSegmentId) => {
  const allSegments = useSelector((state) => state.sealink.segments);
  const allRoutes = useSelector((state) => state.routes);

  const getStopsByRouteId = (routeId) => allRoutes[routeId].route_stops;

  const forwardSegment = allSegments[forwardSegmentId];
  const returnSegment = allSegments[returnSegmentId];

  const toIslandRouteId = forwardSegment.qt_route_id;
  const fromIslandRouteId = returnSegment.qt_route_id;
  const toIslandStops = getStopsByRouteId(toIslandRouteId);
  const fromIslandStops = getStopsByRouteId(fromIslandRouteId);

  const getStopNameByRouteStopId = (stopId) =>
    toIslandStops.find((stop) => stop.id === stopId)?.name ||
    fromIslandStops.find((stop) => stop.id === stopId)?.name;

  const getStopIdByRouteStopId = (routeStopId) =>
    toIslandStops.find((stop) => stop.id === routeStopId)?.stop_id ||
    fromIslandStops.find((stop) => stop.id === routeStopId)?.stop_id;

  const buildData = (data) => ({
    departureTime: parseTime(data?.departs_at),
    departureDate: data?.departs_at.split('T')[0],
    departureDescription: getStopNameByRouteStopId(data?.from_route_stop_id),
    arrivalTime: parseTime(data?.arrives_at),
    arrivalDescription: getStopNameByRouteStopId(data?.to_route_stop_id),
    isBoarding: data.is_boarding,
    available: data.available,
  });

  let toIslandStopIds = [toIslandStops[0].stop_id];
  if (forwardSegment.subroutes?.length > 0) {
    toIslandStopIds = uniq(
      forwardSegment.subroutes
        .map((subroute) =>
          getStopIdByRouteStopId(subroute.from_route_stop_qt_id)
        )
        .filter(Boolean)
    );
  }

  let fromIslandStopIds = [fromIslandStops[0].stop_id];
  if (returnSegment.subroutes?.length > 0) {
    fromIslandStopIds = uniq(
      returnSegment.subroutes
        .map((subroute) =>
          getStopIdByRouteStopId(subroute.from_route_stop_qt_id)
        )
        .filter(Boolean)
    );
  }

  const toIslandParams = {
    route_id: toIslandRouteId,
    from_stop_ids: toIslandStopIds,
    limit: 5,
  };
  const fromIslandParams = {
    route_id: fromIslandRouteId,
    from_stop_ids: fromIslandStopIds,
    limit: 5,
  };

  const {
    data: resOfToIsland,
    isFetching: isFetchingToIslandService,
    error: error1,
  } = useNextDeparturesQuery(toIslandParams);
  const {
    data: resOfFromIsland,
    isFetching: isFetchingFromIslandService,
    error: error2,
  } = useNextDeparturesQuery(fromIslandParams);

  const error = error1 || error2;
  if (error) {
    trackError(error);
  }

  let toIslandService = {};
  let fromIslandService = {};
  const firstAvailableDepartureToIsland =
    getFirstAvailableDeparture(resOfToIsland);
  if (!isEmpty(firstAvailableDepartureToIsland)) {
    toIslandService = buildData(firstAvailableDepartureToIsland);
  }
  const firstAvailableDepartureFromIsland =
    getFirstAvailableDeparture(resOfFromIsland);
  if (!isEmpty(firstAvailableDepartureFromIsland)) {
    fromIslandService = buildData(firstAvailableDepartureFromIsland);
  }

  const departures = [toIslandService, fromIslandService];
  const isLoading = isFetchingToIslandService || isFetchingFromIslandService;

  return [departures, isLoading];
};
